import type {
  ProfessionalContentItem,
  ProfessionalContentPack,
  ProfessionalContentType,
} from "./types";
import {
  APPENDIX_E_CONTENT_TARGETS,
  buildCommonWordItems,
  expandLaunchContentItems,
  getAppendixEContentTarget,
  type LaunchContentItemInput,
} from "./launch-content";
import { ADAPTIVE_TEMPLATE_CATALOG } from "../adaptive/catalog";

const MASTER_TYPING_LICENSE = {
  id: "mastertyping-original-v1",
  name: "Original MasterTyping content — Sixsmith Games",
} as const;

const CONTENT_VERSION = "2.0.0";
const CONTENT_LOCALE = "en-US";
const CONTENT_LAYOUTS = ["us-qwerty", "dvorak", "colemak"];

type ContentItemInput = LaunchContentItemInput;

type ContentPackInput = {
  id: string;
  title: string;
  description: string;
  category: ProfessionalContentPack["category"];
  contentType: ProfessionalContentType;
  items: ContentItemInput[];
};

function uniqueCharacters(text: string): string[] {
  return [...new Set([...text])].sort((left, right) =>
    left.localeCompare(right, CONTENT_LOCALE),
  );
}

function representativeWords(text: string): string[] {
  return [
    ...new Set(
      text
        .toLocaleLowerCase(CONTENT_LOCALE)
        .match(/[a-z]+(?:['’-][a-z]+)?/g) ?? [],
    ),
  ].slice(0, 24);
}

function contentItem(input: ContentItemInput): ProfessionalContentItem {
  return {
    ...input,
    version: CONTENT_VERSION,
    source: input.source ?? "Original MasterTyping editorial content",
    author: "Sixsmith Games",
    licenseId: MASTER_TYPING_LICENSE.id,
    locale: CONTENT_LOCALE,
    keyboardLayouts: [...CONTENT_LAYOUTS],
    characterCoverage: uniqueCharacters(input.text),
    wordCoverage: representativeWords(input.text),
    patternCoverage: [...input.focusSkills],
    validationStatus: "validated",
    reviewStatus: "editorial-review-pending",
    syntaxValidated: input.syntaxValidated ?? Boolean(input.language),
    benchmarkApproved: input.benchmarkApproved ?? false,
  };
}

function contentPack(input: ContentPackInput): ProfessionalContentPack {
  const target = getAppendixEContentTarget(input.id);
  if (!target) {
    throw new Error(`Missing launch content target for ${input.id}.`);
  }
  const expandedItems = expandLaunchContentItems(input.id, input.items);
  const actualInventory =
    target.unit === "words"
      ? new Set(
          expandedItems.flatMap((item) =>
            item.text.toLocaleLowerCase(CONTENT_LOCALE).match(/[a-z]+(?:['-][a-z]+)?/g) ?? [],
          ),
        ).size
      : expandedItems.length;
  return {
    id: input.id,
    version: CONTENT_VERSION,
    title: input.title,
    description: input.description,
    category: input.category,
    status: actualInventory >= target.target ? "release-candidate" : "validated-beta",
    locale: CONTENT_LOCALE,
    keyboardLayouts: [...CONTENT_LAYOUTS],
    contentType: input.contentType,
    inventoryUnit: target.unit,
    launchTarget: target.target,
    requiredPatterns: [...target.requiredPatterns],
    reviewStatus: "editorial-review-pending",
    licenses: [{ ...MASTER_TYPING_LICENSE }],
    items: expandedItems.map(contentItem),
  };
}

export const PROFESSIONAL_CONTENT_PACKS: readonly ProfessionalContentPack[] = [
  contentPack({
    id: "common-words",
    title: "Common words",
    description: "One thousand frequency-progressive English words for word-count fluency.",
    category: "words",
    contentType: "words",
    items: buildCommonWordItems(),
  }),
  contentPack({
    id: "prose-flow",
    title: "Natural prose",
    description: "Readable workplace prose for rhythm, accuracy, and endurance.",
    category: "prose",
    contentType: "sentences",
    items: [
      {
        id: "prose-clear-work",
        title: "Clear work",
        text: "Clear writing begins with a useful idea and a patient first draft. The writer shapes each sentence, checks its purpose, and removes anything that makes the message harder to follow. Reliable typing supports that process by keeping attention on the thought instead of the keyboard.",
        focusSkills: ["rhythm", "common-bigrams"],
        difficulty: "standard",
      },
      {
        id: "prose-steady-craft",
        title: "Steady craft",
        text: "A steady pace is built through relaxed motion, accurate reaches, and enough practice to make difficult transitions feel ordinary. Small gains matter because they accumulate. When errors appear, correct them calmly, notice the pattern, and return to the sentence without rushing.",
        focusSkills: ["accuracy", "rhythm"],
        difficulty: "foundation",
      },
      {
        id: "prose-benchmark-60",
        title: "Standard one-minute benchmark",
        text: "Every reliable benchmark begins with fair timing and a calm approach. The goal is not to force an impressive number, but to collect useful evidence about speed and accuracy under consistent conditions. Read slightly ahead, keep each movement light, and let a steady rhythm carry the sentence forward. When punctuation appears, preserve the same control used for ordinary letters. Trustworthy progress becomes visible across several comparable sessions, not through one lucky burst. A useful result reflects ordinary technique: clear attention, honest correction, and enough measured activity to support the conclusion. Finish with the same control you used at the start.",
        focusSkills: ["benchmark", "rhythm", "punctuation"],
        difficulty: "standard",
        benchmarkApproved: true,
      },
      {
        id: "prose-benchmark-continuation-a",
        title: "Standard benchmark continuation A",
        text: "Good measurement depends on ordinary conditions that another session can reproduce. The chair, display, keyboard, and assistance settings should remain comfortable and known. If an interruption changes the attempt, record it rather than hiding it. A benchmark is useful when its limits are visible, its rules remain stable, and its result can be compared without pretending that every day feels exactly the same.",
        focusSkills: ["benchmark", "rhythm", "common-words"],
        difficulty: "standard",
        benchmarkApproved: true,
      },
      {
        id: "prose-benchmark-continuation-b",
        title: "Standard benchmark continuation B",
        text: "Consistent technique is easier to evaluate than a frantic peak. Keep the hands relaxed, allow the eyes to move ahead, and correct mistakes without turning one error into several. The final number is evidence, not a verdict. It becomes meaningful beside earlier attempts completed with the same duration, text version, keyboard layout, timing rules, and verification requirements.",
        focusSkills: ["benchmark", "accuracy", "reading-ahead"],
        difficulty: "standard",
        benchmarkApproved: true,
      },
      {
        id: "prose-benchmark-continuation-c",
        title: "Standard benchmark continuation C",
        text: "Progress rarely follows a perfectly straight line. Sleep, familiarity, attention, and recent practice can influence a single attempt, so the strongest conclusion comes from a series of comparable results. Look for a durable change in accurate pace while control remains high. When accuracy falls, treat that signal as guidance to slow down and rebuild the movement before adding pressure.",
        focusSkills: ["benchmark", "accuracy", "punctuation"],
        difficulty: "standard",
        benchmarkApproved: true,
      },
      {
        id: "prose-morning-plan",
        title: "A deliberate morning",
        text: "Before the messages arrive, Rowan reviews the day and chooses one task that deserves uninterrupted attention. The plan is modest: finish the outline, answer the urgent questions, and leave enough space for careful revision. A clear sequence reduces friction and makes steady work easier to sustain.",
        focusSkills: ["common-words", "sentence-flow"],
        difficulty: "foundation",
      },
      {
        id: "prose-useful-meeting",
        title: "A useful meeting",
        text: "The most useful meetings begin with a decision to make, not a calendar slot to fill. Participants receive the context early, bring the evidence they own, and leave with named actions. Brief notes capture what changed, who will respond, and when the group should check progress again.",
        focusSkills: ["rhythm", "word-endings"],
        difficulty: "standard",
      },
      {
        id: "prose-library-window",
        title: "Library window",
        text: "Rain moved softly across the library windows while the last readers gathered their books. At the long table, a student compared two sources and marked a question in the margin. The quiet room did not remove every distraction, but it made thoughtful concentration feel possible.",
        focusSkills: ["left-right-alternation", "common-bigrams"],
        difficulty: "foundation",
      },
      {
        id: "prose-careful-review",
        title: "Careful review",
        text: "A careful review separates correctness from preference. First confirm the facts, the required behavior, and the reader's likely path. Then improve the wording without turning every sentence into the reviewer's personal style. Specific comments create movement; vague criticism only creates uncertainty.",
        focusSkills: ["apostrophe", "rhythm"],
        difficulty: "advanced",
      },
      {
        id: "prose-small-experiment",
        title: "Small experiment",
        text: "The team tested the smallest change that could answer its question. They wrote down the expected result, observed what people actually did, and resisted the urge to explain away an inconvenient signal. One limited experiment produced better direction than a month of confident guessing.",
        focusSkills: ["accuracy", "suffixes"],
        difficulty: "standard",
      },
      {
        id: "prose-garden-route",
        title: "Garden route",
        text: "The route behind the community garden curved past raised beds, a tool shed, and a row of young maples. Volunteers had placed simple signs beside each plot so visitors could identify herbs and seasonal vegetables. Even a short walk offered details that were easy to miss at speed.",
        focusSkills: ["common-words", "reading-ahead"],
        difficulty: "foundation",
      },
      {
        id: "prose-reliable-handoff",
        title: "Reliable handoff",
        text: "A reliable handoff gives the next person enough context to act without reconstructing the entire project. It names the current state, the unresolved risk, the recent decision, and the next checkpoint. Links support the summary, but they do not replace a clear explanation of what matters.",
        focusSkills: ["rhythm", "common-trigrams"],
        difficulty: "standard",
      },
      {
        id: "prose-ferry-crossing",
        title: "Ferry crossing",
        text: "By late afternoon, the ferry had crossed the bright channel and slowed beside the harbor wall. Travelers stepped onto the pier with bags, bicycles, and folded maps. The crew checked the quiet deck once more before preparing the vessel for its final trip home.",
        focusSkills: ["left-right-alternation", "word-endings"],
        difficulty: "foundation",
      },
      {
        id: "prose-support-note",
        title: "Support note",
        text: "A strong support note describes what the customer attempted, what the system returned, and which recovery steps already failed. It avoids blame and records exact conditions without exposing private data. The next teammate can then continue the investigation instead of asking the customer to begin again.",
        focusSkills: ["accuracy", "sentence-flow"],
        difficulty: "advanced",
      },
      {
        id: "prose-decision-record",
        title: "Decision record",
        text: "The decision record is short because its job is precise. It states the problem, lists the practical options, explains the chosen tradeoff, and names the date for review. Future readers may disagree with the choice, yet they can still understand why it was reasonable at the time.",
        focusSkills: ["common-bigrams", "punctuation"],
        difficulty: "standard",
      },
      {
        id: "prose-workshop-bench",
        title: "Workshop bench",
        text: "On the workshop bench, every tool had a visible place and every unfinished piece carried a small paper label. The system was not decorative; it prevented confusion when several people shared the room. Order saved time, protected delicate work, and made the next step obvious.",
        focusSkills: ["common-words", "rhythm"],
        difficulty: "foundation",
      },
      {
        id: "prose-editing-distance",
        title: "Editing with distance",
        text: "After finishing a draft, the editor stepped away long enough to forget the exact shape of each sentence. On returning, missing links and repeated phrases became easier to notice. Distance did not solve the argument, but it restored the curiosity required to examine it honestly.",
        focusSkills: ["suffixes", "reading-ahead"],
        difficulty: "advanced",
      },
      {
        id: "prose-field-observation",
        title: "Field observation",
        text: "During the field visit, Priya recorded what she could see before adding an interpretation. She noted the sequence of events, the conditions around them, and the limits of her vantage point. Separating observation from explanation made the later analysis more credible.",
        focusSkills: ["capitalization", "common-trigrams"],
        difficulty: "standard",
      },
      {
        id: "prose-community-radio",
        title: "Community radio",
        text: "The community radio studio occupied two bright rooms above the old market. Volunteers planned interviews, checked pronunciations, and learned how silence could improve a conversation. Each weekly program sounded effortless only because the team had prepared its transitions with care.",
        focusSkills: ["rhythm", "word-endings"],
        difficulty: "standard",
      },
      {
        id: "prose-restored-trail",
        title: "Restored trail",
        text: "Months of patient work reopened the trail without erasing its natural character. The crew redirected water, replaced unsafe boards, and trimmed only the branches that blocked the route. Hikers could move safely while the wetland beside them remained quiet and protected.",
        focusSkills: ["left-right-alternation", "accuracy"],
        difficulty: "foundation",
      },
      {
        id: "prose-design-critique",
        title: "Design critique",
        text: "During critique, the designer asked colleagues to follow a real task rather than judge a static screen. Their questions revealed where labels were unclear and where visual emphasis contradicted the intended order. The revision became simpler because the feedback stayed connected to observable use.",
        focusSkills: ["common-bigrams", "reading-ahead"],
        difficulty: "advanced",
      },
      {
        id: "prose-evening-kitchen",
        title: "Evening kitchen",
        text: "The kitchen settled into a practiced rhythm as the evening orders arrived. One cook watched the oven, another prepared fresh plates, and a third called the timing in a steady voice. Speed mattered, but accuracy kept every table moving and every ingredient accounted for.",
        focusSkills: ["rhythm", "common-words"],
        difficulty: "foundation",
      },
      {
        id: "prose-maintenance-window",
        title: "Maintenance window",
        text: "The maintenance window began only after the rollback steps were tested and the responsible people confirmed their roles. Updates moved in small stages while the team watched the service indicators. When one signal drifted, they paused the change, investigated, and protected the stable system.",
        focusSkills: ["accuracy", "technical-prose"],
        difficulty: "advanced",
      },
      {
        id: "prose-oral-history",
        title: "Oral history",
        text: "An oral history interview depends on listening more than filling silence. The researcher begins with open questions, follows the speaker's language, and distinguishes memory from verified fact in the final notes. Respectful preparation allows a personal account to retain its texture and context.",
        focusSkills: ["apostrophe", "sentence-flow"],
        difficulty: "advanced",
      },
      {
        id: "prose-calm-incident",
        title: "Calm incident response",
        text: "When the alert arrived, the responder checked the customer impact before changing the system. A calm timeline kept observations separate from assumptions and recorded who owned each action. Fast work became safer because the team communicated clearly and limited each intervention.",
        focusSkills: ["common-trigrams", "rhythm"],
        difficulty: "standard",
      },
      {
        id: "prose-print-studio",
        title: "Print studio",
        text: "In the print studio, test sheets covered one wall from floor to ceiling. The artist compared ink density, paper texture, and the smallest readable details before approving the edition. Repetition was part of the craft, but every trial answered a specific visual question.",
        focusSkills: ["left-right-alternation", "word-endings"],
        difficulty: "standard",
      },
      {
        id: "prose-learning-log",
        title: "Learning log",
        text: "A useful learning log records more than a score. It names the strategy that was attempted, the place where control weakened, and one adjustment for the next session. Over time, these brief observations reveal whether practice is solving the actual problem or merely repeating it.",
        focusSkills: ["accuracy", "common-words"],
        difficulty: "foundation",
      },
      {
        id: "prose-map-room",
        title: "Map room",
        text: "The map room held layers of the same coastline drawn decades apart. Comparing them showed where roads had shifted, marshes had returned, and neighborhoods had expanded. No single map explained the whole place; the useful story appeared through patient comparison.",
        focusSkills: ["rhythm", "reading-ahead"],
        difficulty: "standard",
      },
      {
        id: "prose-confident-pause",
        title: "A confident pause",
        text: "Fluent typing does not require constant motion. A brief pause to read ahead can prevent a longer chain of corrections, especially when the sentence changes direction. Confidence comes from choosing the next movement clearly, not from treating every moment of stillness as failure.",
        focusSkills: ["reading-ahead", "accuracy"],
        difficulty: "advanced",
      },
      {
        id: "prose-public-garden",
        title: "Public garden plan",
        text: "The new garden plan balanced shaded seating, open paths, native plants, and easy access to water. Residents tested the proposed route with carts, wheelchairs, and small children before construction began. Their practical observations improved both the beauty and usefulness of the space.",
        focusSkills: ["common-bigrams", "sentence-flow"],
        difficulty: "standard",
      },
      {
        id: "prose-final-check",
        title: "Final check",
        text: "Before sending the work, Elena checked the names, dates, links, and requested format one final time. She read the opening aloud, confirmed the main action, and removed a paragraph that repeated an earlier point. The quiet review took minutes and prevented hours of avoidable confusion.",
        focusSkills: ["accuracy", "capitalization"],
        difficulty: "foundation",
      },
    ],
  }),
  contentPack({
    id: "shift-punctuation",
    title: "Shift & punctuation",
    description: "Capitals, paired symbols, quotations, and sentence punctuation in context.",
    category: "punctuation",
    contentType: "sentences",
    items: [
      { id: "punctuation-editorial", title: "Editorial marks", text: "Maya asked, “Is the 4:30 review still confirmed?” Ben replied, “Yes—bring the Q2 notes, the draft agenda, and Sam’s final chart.”", focusSkills: ["shift", "punctuation", "quotes"], difficulty: "advanced" },
      { id: "punctuation-questions", title: "Questions and clauses", text: "Before publishing, check three things: Is the claim clear? Does the evidence support it? Can a new reader follow the conclusion without extra context?", focusSkills: ["shift", "colon", "question-mark"], difficulty: "standard" },
      { id: "punctuation-parenthetical", title: "Parenthetical detail", text: "The revised estimate (which includes delivery, setup, and testing) is ready; however, the optional training session remains outside the total.", focusSkills: ["parentheses", "semicolon", "comma"], difficulty: "advanced" },
      { id: "punctuation-possessives", title: "Possessives and contractions", text: "Jordan’s notes weren’t in the shared folder, so I’ll check the team’s archive before asking anyone to upload another copy.", focusSkills: ["apostrophe", "shift", "comma"], difficulty: "standard" },
      { id: "punctuation-list", title: "Parallel list", text: "The proposal has four goals: reduce delay, clarify ownership, protect private data, and make every recovery step visible.", focusSkills: ["colon", "comma", "sentence-flow"], difficulty: "foundation" },
      { id: "punctuation-dashes", title: "Dashes and emphasis", text: "The first result looked convincing—but one missing control changed the conclusion. We repeated the test, documented the difference, and updated the recommendation.", focusSkills: ["em-dash", "shift", "period"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "numbers-data",
    title: "Numbers & dates",
    description: "Dates, times, money, percentages, measurements, and practical data entry.",
    category: "numbers",
    contentType: "sentences",
    items: [
      { id: "numbers-operations", title: "Operations report", text: "On 07/29/2026, the team processed 1,248 orders, resolved 96.4% by 5:30 PM, and reduced the open balance from $18,750.00 to $6,405.25.", focusSkills: ["number-row", "dates", "currency", "percent"], difficulty: "advanced" },
      { id: "numbers-schedule", title: "Schedule data", text: "Route 14 leaves at 08:15, arrives at 10:42, and covers 127.6 miles. Allow 45 minutes for the transfer at platform 3.", focusSkills: ["number-row", "time", "decimal"], difficulty: "standard" },
      { id: "numbers-inventory", title: "Inventory count", text: "Warehouse B holds 640 units: 315 in aisle 2, 184 in aisle 7, and 141 in the secure cabinet. Reorder when the total falls below 225.", focusSkills: ["number-row", "colon", "thresholds"], difficulty: "foundation" },
      { id: "numbers-temperature", title: "Measurement log", text: "At 06:00, the sensor read 18.7°C; by 13:45, it reached 24.2°C. Humidity changed from 61% to 54% during the same interval.", focusSkills: ["decimals", "degree-symbol", "percent", "time"], difficulty: "advanced" },
      { id: "numbers-budget", title: "Budget summary", text: "The $72,500 budget assigns 42% to labor, 28% to materials, 18% to equipment, and 12% to contingency. The first payment is due 09/15/2026.", focusSkills: ["currency", "percent", "dates"], difficulty: "advanced" },
      { id: "numbers-reference", title: "Reference codes", text: "Check invoice 004918 against order PO-7832. The shipment contains 24 cartons, 6 items per carton, with a recorded mass of 318.5 kg.", focusSkills: ["leading-zeroes", "hyphen", "units"], difficulty: "standard" },
    ],
  }),
  contentPack({
    id: "symbols-operators",
    title: "Symbols & operators",
    description: "Focused symbol sequences embedded in readable technical instructions.",
    category: "punctuation",
    contentType: "short-bursts",
    items: [
      { id: "symbols-comparison", title: "Comparison operators", text: "Compare a >= 12, b <= 24, and a != b; record true only when (a + b) / 2 > 10.", focusSkills: ["operators", "number-row", "parentheses"], difficulty: "advanced" },
      { id: "symbols-paths", title: "Paths and flags", text: "Run tool --input ./drafts/report.md --output ../exports/report.html --force=false", focusSkills: ["hyphen", "slash", "period", "equals"], difficulty: "advanced" },
      { id: "symbols-contact", title: "Contact notation", text: "Send updates to ops@example.test, tag @release-team, and link issue #482 before 16:00.", focusSkills: ["at-sign", "hash", "hyphen", "colon"], difficulty: "standard" },
      { id: "symbols-grouping", title: "Grouping marks", text: "Group the values as {alpha: [1, 2, 3], beta: (4 + 5), ready: true}. Then verify each closing mark.", focusSkills: ["braces", "brackets", "parentheses", "colon"], difficulty: "standard" },
      { id: "symbols-navigation", title: "Navigation keys", text: "Use Ctrl+Shift+P, choose Tools > Review, then select Find & Replace… to update the label.", focusSkills: ["plus", "greater-than", "ampersand", "ellipsis"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "writing-dialogue",
    title: "Writing & dialogue",
    description: "Natural dialogue, attribution, revision, and varied sentence structure.",
    category: "punctuation",
    contentType: "sentences",
    items: [
      { id: "dialogue-arrival", title: "Late arrival", text: "“I thought the train left at nine,” Amira said. “It does,” Luis replied, checking the board, “but our platform changed ten minutes ago.”", focusSkills: ["quotes", "comma", "shift"], difficulty: "advanced" },
      { id: "dialogue-direct", title: "Direct answer", text: "The editor asked whether the opening was necessary. Priya answered directly: “Keep the example, but cut the first two sentences.”", focusSkills: ["quotes", "colon", "capitalization"], difficulty: "standard" },
      { id: "dialogue-interruption", title: "Interrupted thought", text: "“If the forecast holds—” Theo began. A sharp knock interrupted him. “Then we leave before sunrise,” Mara finished.", focusSkills: ["em-dash", "quotes", "sentence-boundaries"], difficulty: "advanced" },
      { id: "writing-revision", title: "Revision note", text: "Original: The tool is very easy to use. Revision: The guided setup takes about two minutes and explains every required choice.", focusSkills: ["colon", "specific-language", "capitalization"], difficulty: "foundation" },
      { id: "writing-transition", title: "Clear transition", text: "The pilot reduced waiting time. However, it did not improve the accuracy of the records; the second phase must address both outcomes.", focusSkills: ["semicolon", "transition-words", "comma"], difficulty: "advanced" },
      { id: "writing-scene", title: "Brief scene", text: "Nora opened the studio door and heard the press settle into silence. On the table, one fresh print waited beneath a clean sheet of paper.", focusSkills: ["rhythm", "capitalization", "comma"], difficulty: "foundation" },
    ],
  }),
  contentPack({
    id: "markdown-docs",
    title: "Markdown",
    description: "Headings, links, lists, emphasis, code spans, and task syntax.",
    category: "code",
    contentType: "code",
    items: [
      { id: "markdown-release", title: "Release notes", language: "markdown", text: "## Release notes\n\n- Added keyboard-first setup\n- Fixed the `saveSession()` retry path\n- See [migration guidance](./docs/migration.md)", focusSkills: ["hash", "brackets", "parentheses", "backticks"], difficulty: "standard" },
      { id: "markdown-checklist", title: "Task checklist", language: "markdown", text: "### Verification\n\n- [x] Typecheck\n- [x] Unit tests\n- [ ] Native screen-reader review\n- [ ] Human visual signoff", focusSkills: ["brackets", "hyphen", "hash"], difficulty: "foundation" },
      { id: "markdown-table", title: "Compact table", language: "markdown", text: "| Gate | Result |\n|---|---|\n| Accuracy | 98.2% |\n| Reflow | Pass |\n| Offline | Pass |", focusSkills: ["pipe", "hyphen", "percent"], difficulty: "advanced" },
      { id: "markdown-quote", title: "Quote and emphasis", language: "markdown", text: "> Prefer **clear evidence** over _confident guesses_.\n\nUse `npm run test` before opening the review.", focusSkills: ["greater-than", "asterisk", "underscore", "backticks"], difficulty: "advanced" },
      { id: "markdown-frontmatter", title: "Document front matter", language: "markdown", text: "---\ntitle: Practice Guide\nstatus: draft\ntags: [typing, accessibility]\n---\n\n# Start with accuracy", focusSkills: ["hyphen", "colon", "brackets", "hash"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "coding-symbols",
    title: "JavaScript & TypeScript",
    description: "Valid JavaScript and TypeScript with indentation, operators, and braces.",
    category: "code",
    contentType: "code",
    items: [
      { id: "code-js-filter", title: "JavaScript filter", language: "javascript", text: "const activeNames = users\n  .filter((user) => user.active)\n  .map((user) => user.name.trim());\n\nreturn activeNames.join(\", \");", focusSkills: ["braces", "arrows", "indentation", "dot-access"], difficulty: "advanced" },
      { id: "code-js-function", title: "JavaScript function", language: "javascript", text: "function clamp(value, minimum, maximum) {\n  return Math.min(maximum, Math.max(minimum, value));\n}", focusSkills: ["parentheses", "braces", "indentation"], difficulty: "standard" },
      { id: "code-ts-interface", title: "TypeScript interface", language: "typescript", text: "interface SessionResult {\n  id: string;\n  accurateWpm: number | null;\n  verified: boolean;\n}\n", focusSkills: ["braces", "colon", "semicolon", "pipe"], difficulty: "standard" },
      { id: "code-js-destructure", title: "Safe destructuring", language: "javascript", text: "const { title, status = \"draft\" } = record;\nconst label = `${title} · ${status.toUpperCase()}`;", focusSkills: ["braces", "template-literal", "dot-access"], difficulty: "advanced" },
      { id: "code-ts-guard", title: "Type guard", language: "typescript", text: "function isNumber(value: unknown): value is number {\n  return typeof value === \"number\" && Number.isFinite(value);\n}", focusSkills: ["colon", "braces", "operators", "indentation"], difficulty: "advanced" },
      { id: "code-js-reduce", title: "Reduce values", language: "javascript", text: "const total = prices.reduce((sum, price) => {\n  return sum + price;\n}, 0);", focusSkills: ["arrows", "braces", "comma", "indentation"], difficulty: "standard" },
    ],
  }),
  contentPack({
    id: "python-code",
    title: "Python",
    description: "Valid Python functions, collections, comprehensions, and control flow.",
    category: "code",
    contentType: "code",
    items: [
      { id: "python-normalize", title: "Normalize names", language: "python", text: "def normalize_names(names):\n    return [name.strip().title() for name in names if name.strip()]", focusSkills: ["colon", "brackets", "indentation", "parentheses"], difficulty: "standard" },
      { id: "python-enumerate", title: "Enumerate tasks", language: "python", text: "for index, task in enumerate(tasks, start=1):\n    print(f\"{index}. {task['title']}\")", focusSkills: ["colon", "quotes", "brackets", "indentation"], difficulty: "advanced" },
      { id: "python-dictionary", title: "Dictionary summary", language: "python", text: "summary = {\n    \"passed\": len(passed),\n    \"failed\": len(failed),\n    \"total\": len(results),\n}", focusSkills: ["braces", "quotes", "colon", "indentation"], difficulty: "standard" },
      { id: "python-safe-division", title: "Safe division", language: "python", text: "def ratio(part, whole):\n    if whole == 0:\n        return None\n    return round(part / whole, 3)", focusSkills: ["colon", "operators", "indentation", "comma"], difficulty: "foundation" },
      { id: "python-path", title: "Path selection", language: "python", text: "from pathlib import Path\n\nreports = sorted(Path(\"reports\").glob(\"*.json\"))\nlatest = reports[-1] if reports else None", focusSkills: ["parentheses", "quotes", "brackets", "dot-access"], difficulty: "advanced" },
      { id: "python-exception", title: "Exception handling", language: "python", text: "try:\n    payload = json.loads(raw_value)\nexcept json.JSONDecodeError as error:\n    raise ValueError(\"Invalid settings data\") from error", focusSkills: ["colon", "parentheses", "dot-access", "indentation"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "web-code",
    title: "HTML & CSS",
    description: "Semantic HTML and readable CSS using common web syntax.",
    category: "code",
    contentType: "code",
    items: [
      { id: "html-status", title: "Accessible status", language: "html", text: "<section aria-labelledby=\"status-title\">\n  <h2 id=\"status-title\">System status</h2>\n  <p role=\"status\">All checks passed.</p>\n</section>", focusSkills: ["angle-brackets", "quotes", "hyphen", "indentation"], difficulty: "standard" },
      { id: "html-form", title: "Labeled field", language: "html", text: "<label for=\"display-name\">Display name</label>\n<input id=\"display-name\" name=\"displayName\" autocomplete=\"name\">", focusSkills: ["angle-brackets", "quotes", "equals", "hyphen"], difficulty: "standard" },
      { id: "css-card", title: "Responsive card", language: "css", text: ".card {\n  display: grid;\n  gap: 1rem;\n  padding: clamp(1rem, 3vw, 2rem);\n  border: 1px solid currentColor;\n}", focusSkills: ["braces", "colon", "semicolon", "parentheses"], difficulty: "advanced" },
      { id: "css-focus", title: "Visible focus", language: "css", text: ".action:focus-visible {\n  outline: 3px solid #48cae4;\n  outline-offset: 3px;\n}", focusSkills: ["colon", "hyphen", "hash", "semicolon"], difficulty: "standard" },
      { id: "html-list", title: "Navigation list", language: "html", text: "<nav aria-label=\"Primary\">\n  <ul>\n    <li><a href=\"/practice\">Practice</a></li>\n    <li><a href=\"/progress\">Progress</a></li>\n  </ul>\n</nav>", focusSkills: ["angle-brackets", "slash", "quotes", "indentation"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "structured-json",
    title: "JSON",
    description: "Valid JSON records with significant punctuation and whitespace.",
    category: "structured-data",
    contentType: "structured-data",
    items: [
      { id: "json-profile", title: "Profile record", language: "json", text: "{\n  \"name\": \"Avery Chen\",\n  \"active\": true,\n  \"score\": 97.5,\n  \"tags\": [\"typing\", \"practice\"]\n}", focusSkills: ["quotes", "braces", "colon", "indentation"], difficulty: "advanced" },
      { id: "json-preferences", title: "Preferences", language: "json", text: "{\n  \"theme\": \"dark\",\n  \"fontScale\": 1.125,\n  \"reducedMotion\": false,\n  \"sounds\": { \"effects\": 0.6, \"music\": 0.2 }\n}", focusSkills: ["quotes", "braces", "decimals", "indentation"], difficulty: "advanced" },
      { id: "json-task", title: "Task record", language: "json", text: "{\n  \"id\": \"task-104\",\n  \"title\": \"Review export\",\n  \"done\": false,\n  \"owners\": [\"Mina\", \"Owen\"]\n}", focusSkills: ["quotes", "brackets", "colon", "comma"], difficulty: "standard" },
      { id: "json-metrics", title: "Metric sample", language: "json", text: "{\n  \"accurateWpm\": 64.2,\n  \"accuracy\": 98.1,\n  \"attempts\": 327,\n  \"verified\": true\n}", focusSkills: ["quotes", "decimals", "colon", "comma"], difficulty: "foundation" },
      { id: "json-endpoint", title: "Endpoint configuration", language: "json", text: "{\n  \"baseUrl\": \"https://api.example.test/v1\",\n  \"timeoutMs\": 5000,\n  \"retry\": { \"limit\": 3, \"backoffMs\": 250 }\n}", focusSkills: ["quotes", "slash", "colon", "braces"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "structured-yaml",
    title: "YAML",
    description: "Readable YAML mappings, sequences, and nested configuration.",
    category: "structured-data",
    contentType: "structured-data",
    items: [
      { id: "yaml-workflow", title: "Build workflow", language: "yaml", text: "name: verify\non: [push]\njobs:\n  test:\n    runs-on: ubuntu-latest\n    steps:\n      - run: npm ci\n      - run: npm test", focusSkills: ["colon", "hyphen", "brackets", "indentation"], difficulty: "advanced" },
      { id: "yaml-profile", title: "Profile settings", language: "yaml", text: "profile:\n  name: Avery Chen\n  locale: en-US\n  preferences:\n    contrast: high\n    motion: reduced", focusSkills: ["colon", "hyphen", "indentation"], difficulty: "foundation" },
      { id: "yaml-services", title: "Service list", language: "yaml", text: "services:\n  - name: web\n    port: 8080\n    healthy: true\n  - name: worker\n    concurrency: 4", focusSkills: ["colon", "hyphen", "numbers", "indentation"], difficulty: "standard" },
      { id: "yaml-release", title: "Release metadata", language: "yaml", text: "release:\n  version: 2.1.0\n  date: 2026-08-01\n  channels: [stable, offline]\n  notes: \"Keyboard-first improvements\"", focusSkills: ["colon", "quotes", "brackets", "dates"], difficulty: "standard" },
      { id: "yaml-access", title: "Access policy", language: "yaml", text: "policy:\n  default: deny\n  allow:\n    - action: read\n      resource: reports/*\n    - action: write\n      resource: drafts/*", focusSkills: ["colon", "hyphen", "slash", "asterisk"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "sql-queries",
    title: "SQL",
    description: "Readable SQL selection, grouping, joins, and safe updates.",
    category: "code",
    contentType: "code",
    items: [
      { id: "sql-filter", title: "Filtered selection", language: "sql", text: "SELECT id, title, created_at\nFROM reports\nWHERE status = 'ready'\nORDER BY created_at DESC;", focusSkills: ["underscore", "quotes", "equals", "semicolon"], difficulty: "foundation" },
      { id: "sql-group", title: "Grouped totals", language: "sql", text: "SELECT category, COUNT(*) AS item_count\nFROM inventory\nGROUP BY category\nHAVING COUNT(*) >= 5;", focusSkills: ["parentheses", "asterisk", "underscore", "operators"], difficulty: "standard" },
      { id: "sql-join", title: "Customer orders", language: "sql", text: "SELECT customers.name, orders.total\nFROM customers\nJOIN orders ON orders.customer_id = customers.id\nWHERE orders.total > 100.00;", focusSkills: ["dot-access", "underscore", "operators", "decimal"], difficulty: "advanced" },
      { id: "sql-update", title: "Safe update", language: "sql", text: "UPDATE tasks\nSET status = 'archived', updated_at = CURRENT_TIMESTAMP\nWHERE completed_at < '2026-01-01' AND status = 'done';", focusSkills: ["quotes", "underscore", "dates", "operators"], difficulty: "advanced" },
      { id: "sql-window", title: "Ranked results", language: "sql", text: "SELECT user_id, accurate_wpm,\n  RANK() OVER (ORDER BY accurate_wpm DESC) AS pace_rank\nFROM verified_results;", focusSkills: ["parentheses", "underscore", "indentation"], difficulty: "advanced" },
      { id: "sql-insert", title: "Parameterized insert", language: "sql", text: "INSERT INTO notes (session_id, label, created_at)\nVALUES (:session_id, :label, CURRENT_TIMESTAMP);", focusSkills: ["parentheses", "colon", "underscore", "comma"], difficulty: "standard" },
    ],
  }),
  contentPack({
    id: "chat-commands",
    title: "Chat & commands",
    description: "Short workplace messages, usernames, channels, and command syntax.",
    category: "chat",
    contentType: "short-bursts",
    items: [
      { id: "chat-release", title: "Release coordination", text: "@team The release is ready for a final check. Please review #ops-notes, then run /approve build-204 before 3:00 PM.", focusSkills: ["short-bursts", "symbols", "shift"], difficulty: "standard" },
      { id: "chat-handoff", title: "Quick handoff", text: "I finished the export fix and linked the test evidence in #release. @Mina, could you verify the Windows path when you start?", focusSkills: ["at-sign", "hash", "question-mark"], difficulty: "standard" },
      { id: "chat-command", title: "Bot command", text: "/remind @support-team \"Review the open accessibility checks\" tomorrow at 09:30", focusSkills: ["slash", "at-sign", "quotes", "time"], difficulty: "advanced" },
      { id: "chat-status", title: "Incident status", text: "Update: service is stable, queued work is processing, and the next checkpoint is 14:15. Please keep discussion in #incident-482.", focusSkills: ["colon", "hash", "hyphen", "numbers"], difficulty: "standard" },
      { id: "chat-polite", title: "Clear request", text: "Could someone review the two unresolved comments before noon? The context is in the pinned message; no meeting is needed.", focusSkills: ["question-mark", "semicolon", "short-bursts"], difficulty: "foundation" },
      { id: "chat-game", title: "Team play", text: "gg! Queue again after a 5-minute break? I’ll switch roles; invite player_two when the lobby opens.", focusSkills: ["apostrophe", "underscore", "question-mark", "short-bursts"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "research-citations",
    title: "Research & citations",
    description: "Notes, source comparison, parenthetical citations, and reference details.",
    category: "prose",
    contentType: "sentences",
    items: [
      { id: "research-source-note", title: "Source note", text: "Field note, 18 June 2026: Interview participants described shorter wait times, but the sample included only returning users. Treat the result as suggestive, not representative.", focusSkills: ["dates", "colon", "research-language"], difficulty: "standard" },
      { id: "research-comparison", title: "Compare findings", text: "Lee (2024) reports a moderate effect in supervised practice, whereas Patel and Gómez (2025) find no durable change after six weeks. The different follow-up periods may explain part of the disagreement.", focusSkills: ["parentheses", "dates", "accented-letters"], difficulty: "advanced" },
      { id: "research-reference", title: "Reference entry", text: "Nguyen, T. R., & Silva, M. (2026). Designing trustworthy practice feedback. Journal of Applied Learning, 14(2), 44–61.", focusSkills: ["ampersand", "parentheses", "period", "en-dash"], difficulty: "advanced" },
      { id: "research-limit", title: "State a limitation", text: "The survey captures reported confidence rather than observed performance. Because responses were voluntary, the results may overrepresent people with unusually positive or negative experiences.", focusSkills: ["research-language", "comma", "sentence-flow"], difficulty: "standard" },
      { id: "research-question", title: "Research question", text: "Question: Does spaced practice improve error correction when total practice time remains constant? Measure immediate accuracy, delayed retention, and confidence separately.", focusSkills: ["colon", "question-mark", "comma"], difficulty: "foundation" },
      { id: "research-synthesis", title: "Evidence synthesis", text: "Across four small studies, the direction of the result is consistent, but the estimated size varies widely. A cautious synthesis should report that uncertainty and avoid presenting the pooled average as universal.", focusSkills: ["research-language", "comma", "rhythm"], difficulty: "advanced" },
    ],
  }),
  contentPack({
    id: "adaptive-problem-keys",
    title: "Adaptive problem keys",
    description: "Readable template inventory for targeted key, bigram, and trigram practice.",
    category: "adaptive",
    contentType: "sentences",
    items: ADAPTIVE_TEMPLATE_CATALOG.map((template) => ({
      id: template.id,
      title: `Adaptive ${template.id.slice(-5)}`,
      text: template.text,
      focusSkills: [
        "key-insertion",
        "bigram-insertion",
        "trigram-insertion",
        template.family,
      ],
      difficulty:
        template.difficulty === 1
          ? "foundation"
          : template.difficulty === 2
            ? "standard"
            : "advanced",
      source: "Original MasterTyping adaptive template catalog",
    })),
  }),
] as const;

export interface ProfessionalContentValidationIssue {
  code:
    | "duplicate-pack-id"
    | "duplicate-item-id"
    | "duplicate-text"
    | "near-duplicate-text"
    | "invalid-pack-metadata"
    | "invalid-item-metadata"
    | "content-too-short"
    | "missing-benchmark"
    | "missing-layout-metadata"
    | "missing-launch-pack"
    | "inventory-below-target"
    | "missing-required-pattern"
    | "unreadable-content"
    | "invalid-syntax-metadata"
    | "review-status-mismatch";
  message: string;
  packId?: string;
  itemId?: string;
}

function normalizedContentIdentity(text: string): string {
  return text
    .normalize("NFKC")
    .toLocaleLowerCase(CONTENT_LOCALE)
    .replace(/\s+/g, " ")
    .trim();
}

const REQUIRED_LAYOUTS = ["us-qwerty", "dvorak", "colemak"] as const;

function inventoryCount(pack: ProfessionalContentPack): number {
  if (pack.inventoryUnit !== "words") return pack.items.length;
  return new Set(
    pack.items.flatMap((item) =>
      item.text.toLocaleLowerCase(CONTENT_LOCALE).match(/[a-z]+(?:['-][a-z]+)?/g) ?? [],
    ),
  ).size;
}

function readabilityIsPlausible(
  item: ProfessionalContentItem,
  pack: ProfessionalContentPack,
): boolean {
  if (["code", "structured-data", "words"].includes(pack.contentType)) return true;
  const words = item.text.match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)?/gu) ?? [];
  if (words.length < (pack.contentType === "short-bursts" ? 3 : 5)) return false;
  const averageWordLength = words.reduce((total, word) => total + [...word].length, 0) / words.length;
  return averageWordLength >= 2.5 && averageWordLength <= 11;
}

function hasEveryLayout(layouts: readonly string[]): boolean {
  return REQUIRED_LAYOUTS.every((layout) => layouts.includes(layout));
}

export interface ProfessionalContentReadiness {
  packId: string;
  actual: number;
  target: number;
  unit: ProfessionalContentPack["inventoryUnit"];
  percent: number;
  targetMet: boolean;
  status: ProfessionalContentPack["status"];
  reviewStatus: ProfessionalContentPack["reviewStatus"];
}

export function getProfessionalContentReadiness(
  packId: string,
): ProfessionalContentReadiness | null {
  const pack = PROFESSIONAL_CONTENT_PACKS.find((entry) => entry.id === packId);
  if (!pack) return null;
  const actual = inventoryCount(pack);
  return {
    packId: pack.id,
    actual,
    target: pack.launchTarget,
    unit: pack.inventoryUnit,
    percent: Math.min(100, Math.round((actual / pack.launchTarget) * 100)),
    targetMet: actual >= pack.launchTarget,
    status: pack.status,
    reviewStatus: pack.reviewStatus,
  };
}

export function validateProfessionalContentLibrary(
  packs: readonly ProfessionalContentPack[] = PROFESSIONAL_CONTENT_PACKS,
): ProfessionalContentValidationIssue[] {
  const issues: ProfessionalContentValidationIssue[] = [];
  const packIds = new Set<string>();
  const itemIds = new Set<string>();
  const contentIdentities = new Set<string>();
  const structuralIdentities = new Map<string, string>();
  let benchmarkCount = 0;

  for (const target of APPENDIX_E_CONTENT_TARGETS) {
    if (!packs.some((pack) => pack.id === target.packId)) {
      issues.push({ code: "missing-launch-pack", message: `Missing launch content pack: ${target.packId}`, packId: target.packId });
    }
  }

  packs.forEach((pack) => {
    if (packIds.has(pack.id)) {
      issues.push({ code: "duplicate-pack-id", message: `Duplicate pack ID: ${pack.id}`, packId: pack.id });
    }
    packIds.add(pack.id);
    if (
      !/^\d+\.\d+\.\d+$/.test(pack.version) ||
      !pack.title.trim() ||
      !pack.description.trim() ||
      !pack.locale ||
      !hasEveryLayout(pack.keyboardLayouts) ||
      pack.licenses.length === 0 ||
      pack.launchTarget < 1 ||
      pack.requiredPatterns.length === 0 ||
      pack.items.length < 4
    ) {
      issues.push({ code: "invalid-pack-metadata", message: `Incomplete pack metadata: ${pack.id}`, packId: pack.id });
    }

    if (!hasEveryLayout(pack.keyboardLayouts)) {
      issues.push({ code: "missing-layout-metadata", message: `Incomplete keyboard-layout metadata: ${pack.id}`, packId: pack.id });
    }

    const actual = inventoryCount(pack);
    if (actual < pack.launchTarget) {
      issues.push({ code: "inventory-below-target", message: `${pack.id} provides ${actual}/${pack.launchTarget} ${pack.inventoryUnit}.`, packId: pack.id });
    }
    const coveredPatterns = new Set(pack.items.flatMap((item) => item.patternCoverage));
    for (const pattern of pack.requiredPatterns) {
      if (!coveredPatterns.has(pattern)) {
        issues.push({ code: "missing-required-pattern", message: `${pack.id} does not cover required pattern ${pattern}.`, packId: pack.id });
      }
    }
    if (
      pack.reviewStatus === "editorial-review-pending" &&
      pack.status === "launch"
    ) {
      issues.push({ code: "review-status-mismatch", message: `${pack.id} cannot be launch while editorial review is pending.`, packId: pack.id });
    }

    const licenses = new Set(pack.licenses.map((license) => license.id));
    pack.items.forEach((item) => {
      if (itemIds.has(item.id)) {
        issues.push({ code: "duplicate-item-id", message: `Duplicate item ID: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      itemIds.add(item.id);
      const identity = normalizedContentIdentity(item.text);
      if (contentIdentities.has(identity)) {
        issues.push({ code: "duplicate-text", message: `Duplicate content text: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      contentIdentities.add(identity);
      const structuralIdentity = identity.replace(/\d+(?:[.,:]\d+)*/g, "#");
      const priorStructuralItem = structuralIdentities.get(`${pack.id}|${structuralIdentity}`);
      if (
        priorStructuralItem &&
        !["numbers", "structured-data"].includes(pack.category)
      ) {
        issues.push({ code: "near-duplicate-text", message: `Near-duplicate content: ${priorStructuralItem} and ${item.id}`, packId: pack.id, itemId: item.id });
      } else {
        structuralIdentities.set(`${pack.id}|${structuralIdentity}`, item.id);
      }
      if (
        pack.contentType !== "words" &&
        item.text.trim().length < (pack.contentType === "short-bursts" ? 20 : 40)
      ) {
        issues.push({ code: "content-too-short", message: `Content item is too short: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      if (
        !/^\d+\.\d+\.\d+$/.test(item.version) ||
        !item.title.trim() ||
        !item.source.trim() ||
        !item.author.trim() ||
        !licenses.has(item.licenseId) ||
        item.locale !== pack.locale ||
        !hasEveryLayout(item.keyboardLayouts) ||
        item.characterCoverage.length === 0 ||
        item.wordCoverage.length === 0 ||
        item.patternCoverage.length === 0 ||
        item.validationStatus !== "validated" ||
        !item.reviewStatus
      ) {
        issues.push({ code: "invalid-item-metadata", message: `Incomplete item metadata: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      if (!hasEveryLayout(item.keyboardLayouts)) {
        issues.push({ code: "missing-layout-metadata", message: `Incomplete item keyboard-layout metadata: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      if (!readabilityIsPlausible(item, pack)) {
        issues.push({ code: "unreadable-content", message: `Content falls outside the readability envelope: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      if (item.language && !item.syntaxValidated) {
        issues.push({ code: "invalid-syntax-metadata", message: `Language content lacks syntax validation: ${item.id}`, packId: pack.id, itemId: item.id });
      }
      if (item.benchmarkApproved) benchmarkCount += 1;
    });
  });

  if (benchmarkCount === 0) {
    issues.push({ code: "missing-benchmark", message: "No approved benchmark content is available." });
  }
  return issues;
}

export function getProfessionalContentInventory(): {
  packCount: number;
  itemCount: number;
  validatedItemCount: number;
  statusCounts: Record<ProfessionalContentPack["status"], number>;
  targetMetPackCount: number;
  totalTargetCount: number;
  readiness: ProfessionalContentReadiness[];
} {
  const items = PROFESSIONAL_CONTENT_PACKS.flatMap((pack) => pack.items);
  const readiness = PROFESSIONAL_CONTENT_PACKS.map((pack) =>
    getProfessionalContentReadiness(pack.id),
  ).filter((entry): entry is ProfessionalContentReadiness => entry !== null);
  return {
    packCount: PROFESSIONAL_CONTENT_PACKS.length,
    itemCount: items.length,
    validatedItemCount: items.filter((item) => item.validationStatus === "validated").length,
    statusCounts: {
      "validated-beta": PROFESSIONAL_CONTENT_PACKS.filter((pack) => pack.status === "validated-beta").length,
      "release-candidate": PROFESSIONAL_CONTENT_PACKS.filter((pack) => pack.status === "release-candidate").length,
      launch: PROFESSIONAL_CONTENT_PACKS.filter((pack) => pack.status === "launch").length,
    },
    targetMetPackCount: readiness.filter((entry) => entry.targetMet).length,
    totalTargetCount: APPENDIX_E_CONTENT_TARGETS.length,
    readiness,
  };
}

export function getProfessionalContentPack(
  packId: string,
): ProfessionalContentPack | null {
  return PROFESSIONAL_CONTENT_PACKS.find((pack) => pack.id === packId) ?? null;
}

export function normalizeProfessionalCustomText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/\u0000/g, "")
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/g, ""))
    .join("\n")
    .trim()
    .slice(0, 12_000);
}
